import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setCallInProgress } from '../../features/channelSlice';
import { playRingingSound, stopRingingSound, playRingingOutSound } from '../../utils/soundUtils';
import { startConversation, sendMessage, closeConversation } from '../../utils/pegaApi';
import authService from '../../services/authService';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';

// Safe audio creation with error handling
const createSafeAudio = (src) => {
    if (typeof Audio === 'undefined') return null;
    try {
        const audio = new Audio(src);
        audio.preload = 'metadata';
        audio.addEventListener('error', (e) => {
            console.debug(`Audio file not available: ${src}`);
        });
        return audio;
    } catch (error) {
        console.debug(`Failed to create audio element for: ${src}`, error);
        return null;
    }
};

// Sound effects
const doneSound = createSafeAudio('/sounds/done.mp3');
const beepSound = createSafeAudio('/sounds/beep.mp3');
const endCallSound = createSafeAudio('/sounds/end-call.mp3');

/**
 * useTurnBasedCall - Hook for managing turn-based voice calls
 *
 * Handles the full STT -> Agent -> TTS flow with:
 * - MediaRecorder-based voice recording
 * - Speech-to-text API calls
 * - Pega Agent conversation management
 * - Text-to-speech playback
 * - Silence detection with 3-level warning escalation
 * - iOS autoplay workarounds
 * - localStorage persistence for call state
 *
 * @param {Object} options
 * @param {Object} options.agentConfig - Agent configuration { agentID, auth, contextID }
 * @param {string} options.pegaContextID - Pega context ID for conversation
 * @param {string} options.hostServer - Pega host server URL
 * @param {string} options.applicationAlias - Pega application alias
 * @param {string} options.backendBaseURL - Backend API base URL
 * @param {boolean} options.enableSoundEffects - Whether to play sound effects
 * @param {Object} options.activeCallContact - The contact being called
 * @param {Object} options.callInfo - Incoming call info (if incoming)
 * @param {boolean} options.isEnvironmentOffline - Whether environment is offline
 */
export function useTurnBasedCall({
    agentConfig,
    pegaContextID,
    hostServer,
    applicationAlias,
    backendBaseURL,
    enableSoundEffects = false,
    activeCallContact,
    callInfo,
    isEnvironmentOffline = false,
}) {
    const dispatch = useDispatch();

    // Determine if outbound call
    const isOutboundCall = !callInfo && activeCallContact;

    // Call state - Initialize from localStorage for persistence
    const [callStatus, setCallStatus] = useState(() => {
        const savedStatus = localStorage.getItem('activeCallStatus');
        return savedStatus || 'Calling...';
    });
    const [callStartTime, setCallStartTime] = useState(() => {
        const savedStartTime = localStorage.getItem('activeCallStartTime');
        return savedStartTime ? parseInt(savedStartTime) : null;
    });
    const [callDuration, setCallDuration] = useState(0);
    const [showKeypad, setShowKeypad] = useState(false);
    const [dialedNumber, setDialedNumber] = useState('');

    // AI Agent state - preserve conversation ID
    const [callConversationID, setCallConversationID] = useState(() => {
        const savedConvID = localStorage.getItem('activeCallConversationID');
        return savedConvID || null;
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [isFirstUserResponse, setIsFirstUserResponse] = useState(() => {
        const savedFirstResponse = localStorage.getItem('activeCallFirstResponse');
        return savedFirstResponse !== 'false';
    });
    const [hasPlayedGreeting, setHasPlayedGreeting] = useState(() => {
        const savedGreeting = localStorage.getItem('activeCallGreeting');
        return savedGreeting === 'true';
    });

    // iOS autoplay workaround state
    const [isAwaitingUserTapForAudio, setIsAwaitingUserTapForAudio] = useState(false);
    const pendingAudioPlayRef = useRef(null);
    const pendingAudioResolveRef = useRef(null);

    // Silence timeout tracking
    const [silenceWarningLevel, setSilenceWarningLevel] = useState(0);
    const silenceWarningLevelRef = useRef(0);
    const silenceTimeoutRef = useRef(null);
    const justPlayedSilenceWarningRef = useRef(false);

    // Audio refs
    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const isInitializingConversation = useRef(false);
    const callConversationIDRef = useRef(localStorage.getItem('activeCallConversationID') || null);
    const callHasEndedRef = useRef(false);

    // Device detection
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isIPad = /iPad/.test(navigator.userAgent) ||
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Silence timeout duration - longer for iPad
    const SILENCE_TIMEOUT_MS = isIPad ? 15000 : 10000;

    // Initialize AudioContext
    useEffect(() => {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const resumeAudioContext = () => {
            if (audioCtx.state === 'suspended') {
                console.log('Resuming AudioContext due to user interaction');
                audioCtx.resume().catch(err => console.error('Failed to resume AudioContext:', err));
            }
        };

        document.addEventListener('click', resumeAudioContext);
        document.addEventListener('touchstart', resumeAudioContext);
        document.addEventListener('touchend', resumeAudioContext);
        document.addEventListener('keydown', resumeAudioContext);

        return () => {
            document.removeEventListener('click', resumeAudioContext);
            document.removeEventListener('touchstart', resumeAudioContext);
            document.removeEventListener('touchend', resumeAudioContext);
            document.removeEventListener('keydown', resumeAudioContext);
            if (audioCtx) audioCtx.close();
        };
    }, []);

    // Handle user tap to play pending audio (iOS workaround)
    useEffect(() => {
        if (!isAwaitingUserTapForAudio) return;

        const handleUserTapForAudio = async (e) => {
            if (e.target.closest('.keypad-button') || e.target.closest('button')) return;

            console.log('User tap detected for pending audio playback');
            e.stopPropagation();

            if (!pendingAudioPlayRef.current) return;

            const { audioElement, text } = pendingAudioPlayRef.current;
            console.log('Playing pending audio on user tap:', text.substring(0, 50));

            setIsAwaitingUserTapForAudio(false);

            try {
                if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                    await audioContextRef.current.resume();
                }

                const playPromise = audioElement.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => console.log('Audio started playing after user tap'))
                        .catch(error => {
                            console.error('Failed to play audio even with user tap:', error);
                            if (pendingAudioResolveRef.current) pendingAudioResolveRef.current();
                            pendingAudioPlayRef.current = null;
                        });
                }
            } catch (error) {
                console.error('Error attempting to play pending audio:', error);
                if (pendingAudioResolveRef.current) pendingAudioResolveRef.current();
                pendingAudioPlayRef.current = null;
            }
        };

        document.addEventListener('click', handleUserTapForAudio, true);
        document.addEventListener('touchstart', handleUserTapForAudio, true);

        return () => {
            document.removeEventListener('click', handleUserTapForAudio, true);
            document.removeEventListener('touchstart', handleUserTapForAudio, true);
        };
    }, [isAwaitingUserTapForAudio]);

    // Play audio response from TTS without resuming listening (for acknowledgment)
    const playAudioOnly = async (text) => {
        console.log('playAudioOnly called with text:', text);

        if (callHasEndedRef.current) {
            console.log('Call has ended - blocking audio playback');
            return;
        }

        try {
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }

            if (isRecording) {
                stopRecording();
                console.log('Paused recording while agent speaks');
            }

            if (enableSoundEffects && doneSound) {
                doneSound.currentTime = 0;
                doneSound.play().catch(e => console.debug('Done sound play failed:', e));
            }

            const token = await authService.getToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const ttsResponse = await fetch(`${backendBaseURL}/api/v1/text-to-speech`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ text }),
            });

            if (!ttsResponse.ok) {
                throw new Error(`Failed to get audio from TTS service: ${ttsResponse.status}`);
            }

            const ttsData = await ttsResponse.json();

            if (audioRef.current) audioRef.current.pause();

            audioRef.current = new Audio(`data:audio/mp3;base64,${ttsData.audioContent}`);
            audioRef.current.onended = () => console.log('Audio finished (no listening resume)');

            await audioRef.current.play();
        } catch (error) {
            console.error('Error playing audio:', error);
        }
    };

    // Play audio response from TTS and resume recording after
    const playAgentResponse = useCallback(async (text, shouldResumeRecording = true) => {
        console.log('playAgentResponse called with text:', text);

        if (callHasEndedRef.current) {
            console.log('Call has ended - blocking audio playback');
            return;
        }

        if (silenceTimeoutRef.current) {
            console.log('Clearing silence timeout - agent speaking');
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }

        try {
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }

            if (isRecording) {
                stopRecording();
                console.log('Paused recording while agent speaks');
            }

            if (enableSoundEffects && doneSound) {
                doneSound.currentTime = 0;
                doneSound.play().catch(e => console.debug('Done sound play failed:', e));
            }

            const token = await authService.getToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const ttsResponse = await fetch(`${backendBaseURL}/api/v1/text-to-speech`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ text }),
            });

            if (!ttsResponse.ok) {
                throw new Error(`Failed to get audio from TTS service: ${ttsResponse.status}`);
            }

            const ttsData = await ttsResponse.json();

            if (!ttsData.audioContent) {
                throw new Error('No audio content in TTS response');
            }

            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }

            const audioDataUrl = `data:audio/mp3;base64,${ttsData.audioContent}`;
            audioRef.current = new Audio();
            audioRef.current.src = audioDataUrl;

            const audioTimeoutMs = 30000;
            let timeoutId = null;
            let hasEnded = false;

            await new Promise((resolve, reject) => {
                const cleanup = () => {
                    if (timeoutId) clearTimeout(timeoutId);
                    if (audioRef.current) {
                        audioRef.current.onplay = null;
                        audioRef.current.onended = null;
                        audioRef.current.onerror = null;
                    }
                };

                audioRef.current.onended = () => {
                    if (!hasEnded) {
                        hasEnded = true;
                        console.log('Agent finished speaking, audio ended');
                        cleanup();
                        if (pendingAudioResolveRef.current) {
                            pendingAudioResolveRef.current();
                            pendingAudioResolveRef.current = null;
                        } else {
                            resolve();
                        }
                    }
                };

                audioRef.current.onerror = (e) => {
                    console.error('Audio error:', audioRef.current?.error?.message);
                    cleanup();
                    reject(new Error(`Audio error: ${audioRef.current?.error?.message}`));
                };

                timeoutId = setTimeout(() => {
                    console.warn('Audio playback timeout - proceeding anyway');
                    cleanup();
                    resolve();
                }, audioTimeoutMs);

                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => console.log('Audio play() promise resolved'))
                        .catch(err => {
                            if (err.name === 'NotAllowedError') {
                                console.log('NotAllowedError on iOS - waiting for user tap');
                                if (timeoutId) clearTimeout(timeoutId);
                                pendingAudioResolveRef.current = resolve;
                                pendingAudioPlayRef.current = { audioElement: audioRef.current, text };
                                setIsAwaitingUserTapForAudio(true);
                            } else {
                                cleanup();
                                reject(err);
                            }
                        });
                }
            });

            console.log('Audio playback complete');

            // Resume recording after agent finishes
            if (isOutboundCall && agentConfig && shouldResumeRecording) {
                const audioDuration = audioRef.current?.duration || 0;
                const baseDelay = isIOS ? 300 : 100;
                const graceperiodForAudio = isIPad ? 800 : 600;
                const extraBufferForLongAudio = audioDuration > 10 ? 500 : 0;
                const delayBeforeRestart = baseDelay + graceperiodForAudio + extraBufferForLongAudio;

                await new Promise(resolve => {
                    setTimeout(() => {
                        if (enableSoundEffects && beepSound) {
                            beepSound.currentTime = 0;
                            beepSound.play().catch(e => console.debug('Beep sound play failed:', e));
                        }

                        try {
                            startRecording();
                            console.log('Resumed recording after agent finished');
                            setIsProcessing(false);
                        } catch (e) {
                            console.error('Failed to resume recording:', e);
                            setIsProcessing(false);
                        }
                        resolve();
                    }, delayBeforeRestart);
                });
            } else {
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Error playing agent response:', error);
            setIsProcessing(false);

            if (isOutboundCall && agentConfig) {
                const baseDelay = isIOS ? 300 : 100;
                const gracePeriodForAudio = isIPad ? 800 : 600;
                const delayBeforeRestart = baseDelay + gracePeriodForAudio;

                await new Promise(resolve => {
                    setTimeout(() => {
                        if (enableSoundEffects && beepSound) {
                            beepSound.currentTime = 0;
                            beepSound.play().catch(e => console.debug('Beep sound play failed:', e));
                        }

                        try {
                            startRecording();
                            console.log('Resumed recording after TTS error');
                            setIsProcessing(false);
                        } catch (e) {
                            console.error('Failed to resume recording after TTS error:', e);
                            setIsProcessing(false);
                        }
                        resolve();
                    }, delayBeforeRestart);
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backendBaseURL, enableSoundEffects, isOutboundCall, agentConfig, isIOS, isIPad]);

    // Handle user speech and send to AI agent
    const handleUserSpeech = async (userTranscript) => {
        if (!isOutboundCall || !agentConfig) return;

        const trimmedTranscript = userTranscript.trim();
        if (!trimmedTranscript || trimmedTranscript.length < 3) {
            console.log('Ignoring short transcript:', trimmedTranscript);
            return;
        }

        if (isProcessing) {
            console.log('Already processing, ignoring new transcript:', trimmedTranscript);
            return;
        }

        setIsProcessing(true);

        try {
            if (!callConversationIDRef.current) {
                if (isInitializingConversation.current) {
                    console.log('Conversation already being initialized, ignoring duplicate');
                    setIsProcessing(false);
                    return;
                }

                isInitializingConversation.current = true;
                console.log('First user response detected:', trimmedTranscript);

                setIsFirstUserResponse(false);

                const acknowledgmentText = "OK, let me just determine how best to assist you. Please bear with me one moment.";
                const acknowledgmentPromise = playAudioOnly(acknowledgmentText);

                const conversationPromise = startConversation(
                    agentConfig.agentID,
                    pegaContextID,
                    agentConfig.auth,
                    hostServer,
                    applicationAlias
                ).then(async (newConversation) => {
                    callConversationIDRef.current = newConversation.ID;
                    setCallConversationID(newConversation.ID);
                    console.log('Started call conversation:', newConversation.ID);

                    return await sendMessage(
                        agentConfig.agentID,
                        newConversation.ID,
                        trimmedTranscript,
                        agentConfig.auth,
                        hostServer,
                        applicationAlias
                    );
                });

                await acknowledgmentPromise;
                const agentResponse = await conversationPromise;
                const responseText = agentResponse.response;
                console.log('Agent first response:', responseText);

                isInitializingConversation.current = false;
                await playAgentResponse(responseText);
            } else {
                console.log('Sending subsequent message:', trimmedTranscript);

                const agentResponse = await sendMessage(
                    agentConfig.agentID,
                    callConversationIDRef.current,
                    trimmedTranscript,
                    agentConfig.auth,
                    hostServer,
                    applicationAlias
                );

                const responseText = agentResponse.response;
                console.log('Agent subsequent response:', responseText);
                await playAgentResponse(responseText);
            }
        } catch (error) {
            console.error('Error in call AI conversation:', error);
            isInitializingConversation.current = false;
            setIsProcessing(false);
        }
    };

    // Handle audio blob from voice recording (speech-to-text)
    const handleStopRecording = async (audioBlob) => {
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');

            const token = await authService.getToken();
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            console.log('Sending audio to speech-to-text API...');
            const response = await fetch(`${backendBaseURL}/api/v1/speech-to-text`, {
                method: 'POST',
                headers,
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`STT failed: ${response.status}`);
            }

            const data = await response.json();
            console.log('Transcript received:', data.transcript);

            const trimmedTranscript = data.transcript?.trim() || '';
            if (trimmedTranscript && trimmedTranscript.length >= 3) {
                clearSilenceTimeout();
                console.log('Valid transcript detected, cleared silence timeout');
            } else {
                console.log('Short/empty transcript detected');
                if (silenceTimeoutRef.current) {
                    clearTimeout(silenceTimeoutRef.current);
                    silenceTimeoutRef.current = null;
                }
                setTimeout(() => {
                    if (isRecording && !isProcessing) {
                        const currentLevel = silenceWarningLevelRef.current;
                        console.log(`Starting silence timeout after user stopped speaking (level ${currentLevel})`);
                        silenceTimeoutRef.current = setTimeout(async () => {
                            let promptText = '';
                            let shouldEndCall = false;
                            let newLevel = currentLevel;

                            switch (currentLevel) {
                                case 0:
                                    promptText = "Are you still there?";
                                    newLevel = 1;
                                    break;
                                case 1:
                                    promptText = "Are you still there? I cannot hear you.";
                                    newLevel = 2;
                                    break;
                                case 2:
                                    promptText = "I am sorry, I'm not able to hear you and am terminating the call.";
                                    shouldEndCall = true;
                                    break;
                                default:
                                    break;
                            }

                            if (promptText && isRecording) {
                                stopRecording();
                                if (!shouldEndCall) {
                                    silenceWarningLevelRef.current = newLevel;
                                    setSilenceWarningLevel(newLevel);
                                }
                                await playAgentResponse(promptText, !shouldEndCall);
                                if (shouldEndCall) {
                                    setTimeout(() => handleEndCall(), 500);
                                }
                            }
                        }, SILENCE_TIMEOUT_MS);
                    }
                }, 10);
            }

            await handleUserSpeech(data.transcript);
        } catch (error) {
            console.error('Error in speech-to-text:', error);
            setIsProcessing(false);
            if (isOutboundCall && agentConfig) {
                startRecording();
            }
        }
    };

    // Use voice recording hook
    const { isRecording, startRecording, stopRecording } =
        useVoiceRecording(handleStopRecording, audioContextRef);

    // Clear silence timeout
    const clearSilenceTimeout = useCallback(() => {
        if (silenceTimeoutRef.current) {
            console.log('Clearing silence timeout');
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }

        if (silenceWarningLevelRef.current > 0) {
            if (justPlayedSilenceWarningRef.current) {
                console.log('Ignoring speech detection - just played silence warning');
            } else {
                console.log('User spoke, resetting silence warning level to 0');
                silenceWarningLevelRef.current = 0;
                setSilenceWarningLevel(0);
            }
        }
    }, []);

    // End call handler
    const handleEndCall = useCallback(async () => {
        console.log('Ending call...');

        callHasEndedRef.current = true;

        if (enableSoundEffects && endCallSound) {
            endCallSound.currentTime = 0;
            endCallSound.play().catch(e => console.debug('End call sound play failed:', e));
        }

        stopRingingSound();
        isInitializingConversation.current = false;

        if (isRecording) {
            stopRecording();
            console.log('Stopped audio recording');
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }

        setIsAwaitingUserTapForAudio(false);
        pendingAudioPlayRef.current = null;
        pendingAudioResolveRef.current = null;

        if (isOutboundCall && callConversationID && agentConfig) {
            try {
                await closeConversation(
                    agentConfig.agentID,
                    callConversationID,
                    agentConfig.auth,
                    hostServer,
                    applicationAlias
                );
                console.log('Closed call conversation:', callConversationID);
                setCallConversationID(null);
            } catch (error) {
                console.error('Error closing call conversation:', error);
            }
        }

        // Clear localStorage
        localStorage.removeItem('activeCallStatus');
        localStorage.removeItem('activeCallStartTime');
        localStorage.removeItem('activeCallConversationID');
        localStorage.removeItem('activeCallFirstResponse');
        localStorage.removeItem('activeCallGreeting');

        dispatch(setCallInProgress(false));
        console.log('Call ended');
    }, [enableSoundEffects, isRecording, stopRecording, isOutboundCall, callConversationID, agentConfig, hostServer, applicationAlias, dispatch]);

    // Handle screen tap for iOS audio
    const handleUserTap = useCallback(() => {
        if (isAwaitingUserTapForAudio && pendingAudioPlayRef.current) {
            // The effect will handle this
        }
    }, [isAwaitingUserTapForAudio]);

    // Keypad press handler
    const handleKeypadPress = useCallback((number) => {
        setDialedNumber(prev => prev + number);
    }, []);

    // Persist call state to localStorage
    useEffect(() => {
        if (callConversationID) {
            localStorage.setItem('activeCallConversationID', callConversationID);
            callConversationIDRef.current = callConversationID;
        }
    }, [callConversationID]);

    useEffect(() => {
        localStorage.setItem('activeCallStatus', callStatus);
    }, [callStatus]);

    useEffect(() => {
        if (callStartTime) {
            localStorage.setItem('activeCallStartTime', callStartTime.toString());
        }
    }, [callStartTime]);

    useEffect(() => {
        localStorage.setItem('activeCallFirstResponse', isFirstUserResponse.toString());
    }, [isFirstUserResponse]);

    useEffect(() => {
        localStorage.setItem('activeCallGreeting', hasPlayedGreeting.toString());
    }, [hasPlayedGreeting]);

    useEffect(() => {
        silenceWarningLevelRef.current = silenceWarningLevel;
    }, [silenceWarningLevel]);

    // Monitor isRecording state to clear silence timeout when recording stops
    useEffect(() => {
        if (!isRecording) {
            if (silenceTimeoutRef.current) {
                console.log('Clearing silence timeout - recording stopped');
                clearTimeout(silenceTimeoutRef.current);
                silenceTimeoutRef.current = null;
            }
        }
    }, [isRecording]);

    // Call duration timer
    useEffect(() => {
        let interval;
        if (callStatus === 'Connected' && callStartTime) {
            const updateDuration = () => {
                const now = Date.now();
                const durationInSeconds = Math.floor((now - callStartTime) / 1000);
                setCallDuration(durationInSeconds);
            };
            updateDuration();
            interval = setInterval(updateDuration, 1000);
        }
        return () => clearInterval(interval);
    }, [callStatus, callStartTime]);

    // Main call connection effect
    useEffect(() => {
        const savedStatus = localStorage.getItem('activeCallStatus');
        const savedContact = localStorage.getItem('activeCallContact');
        const savedCallInfo = localStorage.getItem('callInfo');

        const isReturningToCall = savedStatus === 'Connected' && (savedContact || savedCallInfo);

        if (isReturningToCall) {
            console.log('Returning to existing call, skipping connection sequence');
            setShowKeypad(true);
            return;
        }

        if (!callInfo && !activeCallContact) {
            console.log('No call info or contact, skipping call initialization');
            return;
        }

        console.log('Starting new call connection sequence');

        const isIncomingCall = callInfo && !activeCallContact;

        if (isIncomingCall) {
            console.log('Incoming call detected');
            setCallStatus('Calling...');
            playRingingSound(enableSoundEffects, isEnvironmentOffline);
        } else {
            console.log('Outbound call detected, playing ringing-out sound');
            setCallStatus('Calling...');
            playRingingOutSound(enableSoundEffects, isEnvironmentOffline);
        }

        const callingTimer = setTimeout(async () => {
            console.log('Call connecting timeout triggered');
            setCallStatus('Connected');

            if (!callStartTime) {
                const startTime = Date.now();
                setCallStartTime(startTime);
            }

            stopRingingSound();
            setShowKeypad(true);

            if (isOutboundCall && agentConfig && agentConfig.agentID && !hasPlayedGreeting) {
                console.log('Starting AI conversation for outbound call');
                try {
                    const contactName = activeCallContact?.name || 'Customer Services';
                    const greetingText = `Hi, welcome to ${contactName}, how can I help you today?`;
                    console.log('Playing greeting:', greetingText);
                    await playAgentResponse(greetingText);
                    setHasPlayedGreeting(true);
                    console.log('Greeting completed, waiting for user response...');
                } catch (error) {
                    console.error('Failed to play greeting:', error);
                }
            }
        }, 3000);

        return () => {
            console.log('Cleaning up call connection useEffect');
            clearTimeout(callingTimer);
            stopRingingSound();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callInfo, activeCallContact, isOutboundCall, hasPlayedGreeting, enableSoundEffects, isEnvironmentOffline]);

    // Initialize audio recording for outbound calls
    useEffect(() => {
        if (!isOutboundCall || !agentConfig) return;
        console.log('Audio recording initialized for AI call');

        return () => {
            console.log('Cleaning up audio recording');
            if (isRecording) stopRecording();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOutboundCall, agentConfig]);

    // Derive voice state for UI
    const voiceState = isAwaitingUserTapForAudio ? 'idle' :
                       isProcessing ? 'processing' :
                       isRecording ? 'listening' : 'idle';

    return {
        // Call state
        callStatus,
        callDuration,
        showKeypad,
        dialedNumber,

        // Voice state
        voiceState,
        needsUserTap: isAwaitingUserTapForAudio,
        isRecording,
        isProcessing,

        // Derived
        showAIControls: isOutboundCall && agentConfig && callStatus === 'Connected',

        // Actions
        handleEndCall,
        handleKeypadPress,
        handleUserTap,
    };
}

export default useTurnBasedCall;
